import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import {
  SEGMENT_ENTITY_LABEL,
  type FilterTree,
  type SegmentEntityType,
} from "@colab/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FilterTreeBuilder } from "@/components/shared/saved-views";
import { useUpdateSegment } from "@/features/settings/use-update-segment";

type Props = {
  segmentId: string;
  entityType: SegmentEntityType;
  name: string;
  description: string | null;
  initialFilter: FilterTree;
  canManage: boolean;
};

export function EditSegmentForm({
  segmentId,
  entityType,
  name: initialName,
  description: initialDescription,
  initialFilter,
  canManage,
}: Props) {
  const mutation = useUpdateSegment();
  const readOnly = !canManage;

  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription ?? "");
  const [filter, setFilter] = useState<FilterTree>(initialFilter);

  useEffect(() => {
    setName(initialName);
    setDescription(initialDescription ?? "");
    setFilter(initialFilter);
  }, [initialName, initialDescription, initialFilter]);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    mutation.mutate(
      {
        id: segmentId,
        patch: {
          name: name.trim(),
          description: description.trim() || null,
          filter,
        },
      },
      { onSuccess: () => toast.success("Segment saved") },
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="name" className="text-xs">
            Name
          </Label>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={120}
            disabled={readOnly}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Entity</Label>
          <Input
            value={SEGMENT_ENTITY_LABEL[entityType]}
            disabled
            className="text-muted-foreground"
          />
          <p className="text-[10px] text-muted-foreground">
            Entity is locked after creation — changing it would invalidate the
            filter.
          </p>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="description" className="text-xs">
          Description
        </Label>
        <Textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          maxLength={2000}
          disabled={readOnly}
          placeholder="What this segment is for. Visible to other admins."
        />
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Filter</Label>
        <FilterTreeBuilder
          entityType={entityType}
          value={filter}
          onChange={setFilter}
          disabled={readOnly}
        />
      </div>

      <div className="flex items-center justify-end gap-2 border-t pt-4">
        {readOnly && (
          <p className="mr-auto text-xs text-muted-foreground">
            You need the <code>settings.write</code> permission to edit
            segments.
          </p>
        )}
        <Button type="submit" disabled={mutation.isPending || readOnly}>
          {mutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          Save changes
        </Button>
      </div>
    </form>
  );
}
